import { cn } from "@/lib/utils";
import * as React from "react";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ClientConfigurationRepository from "../../repositories/ClientConfigurationsRepository";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { FilterCombobox, FilterComboboxOption } from "../common/FilterCombobox";

interface ClientConfigSelectorProps {
  activeConfigId: string;
  organizationId: string;
  onConfigChange: (newConfigId: string | null) => void;
  disabled?: boolean;
}

export const ClientConfigSelector: React.FC<ClientConfigSelectorProps> = ({
  organizationId,
  activeConfigId,
  onConfigChange,
  disabled
}) => {
  const [activeOrgId] = useState<string>(organizationId);
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(activeConfigId);
  const clientConfigurationsRepository = new ClientConfigurationRepository();
  const {data: configs = [], isLoading, error} = useQuery({ queryKey: [QUERY_KEYS.CLIENT_CONFIGURATIONS, activeOrgId], queryFn:
    () => clientConfigurationsRepository.getClientConfigurations(activeOrgId), staleTime: 1000 * 60 * 10,
      refetchInterval: 1000 * 60 * 5,
      refetchOnMount: false,
      refetchOnWindowFocus: false, });

  const handleChange = (val: string | null) => {
    setSelectedConfigId(val);
    onConfigChange(val);
  };

  // Build items list: include activeConfigId if not found in configs
  const hasActiveInConfigs = configs.findIndex((config) => config.id === selectedConfigId) > -1;

  const configOptions = useMemo<FilterComboboxOption[]>(() => {
    const opts: FilterComboboxOption[] = [];
    if (activeConfigId && !hasActiveInConfigs && selectedConfigId) {
      opts.push({ value: selectedConfigId, label: selectedConfigId });
    }
    for (const config of configs) {
      if (config.id) {
        opts.push({ value: config.id, label: config.name });
      }
    }
    return opts;
  }, [activeConfigId, hasActiveInConfigs, selectedConfigId, configs]);

  return (
    <div className={cn((isLoading || !!error) && "opacity-50 pointer-events-none")}>
      <FilterCombobox
        disabled={disabled}
        value={selectedConfigId}
        onChange={handleChange}
        placeholder=""
        triggerClassName="h-8"
        options={configOptions}
      />
    </div>
  );
};
